import { PivotModel } from 'api-hooks/common';
import { GearSVG } from 'common/assets';
import { Exact } from 'common/repositories';
import DrawerHeader from 'components/common/drawer/drawer-header';
import PortalModal from 'components/common/portal-modal';
import { Button } from 'components/elements/buttons';
import { camelizeKeys } from 'humps';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import qs from 'qs';
import * as React from 'react';

import PivotControl from './pivot-control';

export interface PivotSettingProps {
  pivot?: PivotModel;
  onPivotChange: (pivot: PivotModel) => void;
}

export default function PivotSetting(props: PivotSettingProps) {
  const { onPivotChange, pivot } = props;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const onOpenPivot = () => setIsOpen(true);

  const onClose = () => setIsOpen(false);

  if (!pivot) {
    return <></>;
  }

  return (
    <>
      <Button
        startEnhancer={(color, size) => <GearSVG color={color} size={size} />}
        onClick={onOpenPivot}
      >
        {t('common:pivot')}
      </Button>
      {isOpen && (
        <PortalModal>
          <div style={styles.content}>
            <DrawerHeader onClose={onClose} title={t('common:setting_pivot')} />
            <div style={styles.bodyContent}>
              <PivotControl pivot={pivot} onPivotChange={onPivotChange} />
            </div>
          </div>
        </PortalModal>
      )}
    </>
  );
}

export function useApplyQueryPivot<
  Query,
  QueryVariables extends Exact<{ input: Exact<{ rows?: string | null }> }>,
>(getPivotMeta: (data: Query) => PivotModel | undefined) {
  const { query } = useRouter();
  const [pivot, setPivot] = React.useState<PivotModel | undefined>();

  const rows: undefined | string = React.useMemo(() => {
    let filterable = false;
    const _rows = (pivot?.value?.rows || []).map((r, i) => {
      if (
        r.name !== pivot?.default.rows[i].name ||
        r.showTotal !== pivot?.default.rows[i].showTotal
      ) {
        filterable = true;
      }

      return {
        name: r.name,
        show_total: !!r.showTotal,
      };
    });

    if (filterable) {
      return _rows as any as string;
    }
  }, [pivot]);

  const parsedQuery = qs.parse(query as unknown as string);

  return {
    input: {
      params: {
        rows: rows || parsedQuery.rows,
      },
    } as unknown as QueryVariables,
    onSuccess(data: Query) {
      setPivot(getPivotMeta(camelizeKeys(data) as any));
    },
    extra: {
      pivot,
      setPivot,
    },
  };
}

const styles: { [key: string]: React.CSSProperties } = {
  fixedContainer: {
    position: 'fixed',
    inset: 0,
    background: '#00000050',
    zIndex: 201,
  },
  relativeContainer: {
    position: 'relative',
  },
  content: {
    position: 'absolute',
    right: 0,
    maxWidth: 400,
    width: '100%',
    backgroundColor: 'white',
    height: '100vh',
  },
  bodyContent: {
    paddingLeft: 16,
    paddingRight: 16,
  },
};
